import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useKillSwitch, useSetKillSwitch } from '../hooks/useOps';

/**
 * Persistent global agent status, rendered on every screen for users who can
 * toggle it (canApprove). Halt is a single confirm; re-arm is stronger (explicit
 * acknowledgement) because it hands action back to the agent. Halting does not
 * touch reads — a human can still query while the agent is halted.
 */
export function KillSwitchBanner() {
  const { data: state } = useKillSwitch();
  const setKill = useSetKillSwitch();
  const [dialog, setDialog] = useState<null | 'halt' | 'resume'>(null);
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);

  if (!state) return null;
  const halted = state.halted;

  const close = () => {
    setDialog(null);
    setReason('');
    setAck(false);
  };

  const confirm = async () => {
    await setKill.mutateAsync({ halted: dialog === 'halt', reason: reason || undefined });
    close();
  };

  return (
    <>
      <Alert
        severity={halted ? 'error' : 'success'}
        variant={halted ? 'filled' : 'standard'}
        icon={halted ? <BlockIcon /> : <PlayArrowIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setDialog(halted ? 'resume' : 'halt')}
          >
            {halted ? 'Resume agent' : 'Halt agent'}
          </Button>
        }
        sx={{ borderRadius: 0 }}
      >
        {halted ? (
          <Box component="span">
            Agent is <strong>HALTED</strong> — all agent mutations are refused.
            {state.reason ? ` Reason: ${state.reason}.` : ''}
            {state.updatedBy ? ` (by ${state.updatedBy})` : ''}
          </Box>
        ) : (
          <Box component="span">Agent is armed — it can take reversible actions autonomously.</Box>
        )}
      </Alert>

      <Dialog open={dialog !== null} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog === 'halt' ? 'Halt the agent?' : 'Resume the agent?'}</DialogTitle>
        <DialogContent>
          {dialog === 'halt' ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                The agent will be refused every mutation immediately. Reads still work. You can
                resume at any time.
              </DialogContentText>
              <TextField
                fullWidth
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Resuming lets the agent act on its own again (update project status, log track/item
                progress, attach photos, mark checklist items). Make sure whatever caused the halt
                is resolved.
              </DialogContentText>
              <FormControlLabel
                control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} />}
                label="I understand the agent will resume acting autonomously."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog === 'halt' ? 'error' : 'primary'}
            disabled={setKill.isPending || (dialog === 'resume' && !ack)}
            onClick={confirm}
          >
            {dialog === 'halt' ? 'Halt agent' : 'Resume agent'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
